<template>
  <!-- Тикет филиала (handoff p4-p5b) -->
  <div class="support-appeal">
    <div v-if="loading && !appeal" class="support-appeal__state">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="notFound" class="support-appeal__state">
      <div class="support-appeal__state-title">
        Обращение не найдено
      </div>
      <div class="support-appeal__state-text">
        Возможно, оно другого филиала. Выберите нужный филиал в шапке или откройте список.
      </div>
      <v-btn depressed class="support-appeal__state-btn text-normalize" @click="goToList">
        Обращения филиала
      </v-btn>
    </div>

    <div v-else-if="error && !appeal" class="support-appeal__state">
      <div class="support-appeal__state-title">
        Не удалось загрузить обращение
      </div>
      <v-btn depressed class="support-appeal__state-btn text-normalize" @click="load">
        Повторить
      </v-btn>
    </div>

    <template v-else-if="appeal">
      <div class="support-appeal__path">
        <button type="button" class="support-appeal__back" @click="goToList">
          <v-icon size="20">
            mdi-arrow-left
          </v-icon>
          Обращения филиала
        </button>
        <span class="support-appeal__slash">/</span>
        <h1 class="support-appeal__title">
          Обращение {{ appeal.number }}
        </h1>
        <span class="support-appeal__chip">
          <span class="support-appeal__chip-dot" :class="`support-appeal__chip-dot--${tone}`" />
          {{ chip }}
        </span>
        <span class="support-appeal__subject">{{ appeal.subject }}</span>
      </div>

      <div class="support-appeal__grid">
        <div class="support-appeal__chat">
          <specialist-plate :appeal="appeal" />
          <feed-list
            :items="items"
            :appeal-number="appeal.number"
            :has-more="hasMore"
            :loading-older="loadingOlder"
            @older="loadOlder"
            @menu="openMenu"
            @photo="viewer = $event"
          >
            <template #after>
              <rating-card
                v-if="rateable"
                :rating="appeal.rating"
                :sending="rating"
                @rate="rate"
              />
            </template>
          </feed-list>
          <reply-field
            ref="reply"
            :placeholder="placeholder"
            :reply-to="replyTo"
            :sending="sending"
            @submit="send"
            @cancel-reply="replyTo = null"
          />
        </div>

        <ticket-panel
          class="support-appeal__panel"
          :appeal="appeal"
          :branch="branch"
          :branch-open="branchOpen"
          :branch-total="branchTotal"
          :solving="solving"
          @solve="solve"
          @open="openOther"
          @all="goToList"
        />
      </div>

      <message-menu :target="menu" @action="menuAction" @close="menu = null" />
      <photo-viewer
        :photos="viewer ? viewer.photos : []"
        :start="viewer ? viewer.index : 0"
        @save="saveImage"
        @close="viewer = null"
      />
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import supportApi, { supportErrorCode, supportErrorText } from '@/api/support'
  import RouteName from '@/models/enums/RouteName'
  import FeedList from '@/components/support/ticket/FeedList.vue'
  import MessageMenu from '@/components/support/ticket/MessageMenu.vue'
  import PhotoViewer from '@/components/support/ticket/PhotoViewer.vue'
  import RatingCard from '@/components/support/ticket/RatingCard.vue'
  import ReplyField from '@/components/support/ticket/ReplyField.vue'
  import SpecialistPlate from '@/components/support/ticket/SpecialistPlate.vue'
  import TicketPanel from '@/components/support/ticket/TicketPanel.vue'
  import { mergeItem } from '@/components/support/feed'
  import { chipStatus, isClosed, statusView } from '@/components/support/status'
  import { branchChannel, isConnected, subscribe } from '@/services/SupportRealtime'

  // Сокет не поднялся - опрос, как в 09-api («Если сокет отвалился…»).
  const POLL_MS = 15000
  // Пачку событий канала (сообщение + appeal.updated) перечитываем одним запросом.
  const REFRESH_DELAY_MS = 400

  export default {
    name: 'SupportAppeal',
    components: { FeedList, MessageMenu, PhotoViewer, RatingCard, ReplyField, SpecialistPlate, TicketPanel },
    props: {
      id: { type: String, required: true },
    },
    data () {
      return {
        appeal: null,
        items: [],
        hasMore: false,
        branchOpen: [],
        branchTotal: 0,
        loading: false,
        loadingOlder: false,
        error: false,
        notFound: false,
        sending: false,
        solving: false,
        rating: false,
        replyTo: null,
        menu: null,
        viewer: null,
        stopRealtime: null,
        pollTimer: null,
        refreshTimer: null,
      }
    },
    computed: {
      ...mapGetters({
        branchId: 'branches/getGlobalFilialId',
        branch: 'support/branch',
        user: 'auth/getUser',
      }),
      tone () {
        return statusView(this.appeal).tone
      },
      chip () {
        return chipStatus(this.appeal)
      },
      rateable () {
        return this.appeal.type === 'issue' && this.appeal.status === 'solved'
      },
      placeholder () {
        if (isClosed(this.appeal)) return 'Если проблема вернётся - напишите сюда, обращение откроется снова'
        const name = this.user?.name

        return name ? `Ответить поддержке… вы пишете как ${name}` : 'Ответить поддержке…'
      },
    },
    created () {
      this.load()
      if (this.branchId) {
        this.stopRealtime = subscribe(branchChannel(this.branchId), {
          'message.created': this.onMessage,
          'appeal.updated': this.scheduleRefresh,
          'appeal.taken': this.scheduleRefresh,
        })
      }
      this.pollTimer = setInterval(this.poll, POLL_MS)
    },
    beforeDestroy () {
      if (this.stopRealtime) this.stopRealtime()
      clearInterval(this.pollTimer)
      clearTimeout(this.refreshTimer)
    },
    methods: {
      async load () {
        this.loading = true
        this.error = false
        this.notFound = false
        try {
          this.apply(await supportApi.appeal(this.id), true)
          this.markRead()
        } catch (e) {
          // Чужой филиал отдаёт тот же 404: обращение есть, но не этого филиала.
          if (supportErrorCode(e) === 'not_found') this.notFound = true
          else this.error = true
        } finally {
          this.loading = false
        }
      },
      apply (data, replaceFeed = false) {
        this.appeal = data.appeal
        this.branchOpen = data.branchOpen
        this.branchTotal = data.branchTotal
        if (replaceFeed) {
          this.items = data.messages
          this.hasMore = data.hasMore
        } else {
          // Перечитывание: свежий хвост ленты вливаем без дублей, подгруженное раньше остаётся.
          this.items = data.messages.reduce(mergeItem, this.items)
        }
      },
      async refresh () {
        try {
          this.apply(await supportApi.appeal(this.id))
        } catch (e) {
          // Тихо: следующий опрос или событие попробует снова.
        }
      },
      scheduleRefresh () {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = setTimeout(this.refresh, REFRESH_DELAY_MS)
      },
      async poll () {
        if (!(await isConnected())) this.refresh()
      },
      // Ответы прочитаны: гаснут счётчики, бейдж «Уведомления» CRM и «N новых» в шторке.
      async markRead () {
        try {
          await supportApi.read(this.id)
        } catch (e) {
          return
        }
        this.$store.dispatch('notifications/requestRefresh')
        this.$store.dispatch('support/loadMe')
      },
      onMessage ({ appealId, message }) {
        if (appealId !== this.id) {
          // Другое обращение филиала: обновятся плитки «Открытые обращения филиала».
          this.scheduleRefresh()
          return
        }
        this.items = mergeItem(this.items, message)
        if (message.from !== 'branch') this.markRead()
        this.scheduleRefresh()
      },
      async loadOlder () {
        const oldest = this.items[0]
        if (!oldest || this.loadingOlder) return
        this.loadingOlder = true
        try {
          const data = await supportApi.olderMessages(this.id, oldest.createdAt)
          this.items = [...data.items, ...this.items]
          this.hasMore = data.hasMore
        } catch (e) {
          this.showErrorMessage(supportErrorText(e))
        } finally {
          this.loadingOlder = false
        }
      },
      async send (payload) {
        this.sending = true
        try {
          const message = await supportApi.send(this.id, payload)
          this.items = mergeItem(this.items, message)
          this.replyTo = null
          this.$refs.reply.reset()
          // Статус мог смениться: «Ждёт вашего ответа» → «В работе», решённое открылось снова.
          this.scheduleRefresh()
        } catch (e) {
          this.showErrorMessage(supportErrorText(e, 'Не отправилось, попробуйте ещё раз'))
        } finally {
          this.sending = false
        }
      },
      async solve () {
        this.solving = true
        try {
          const { appeal } = await supportApi.solve(this.id)
          this.appeal = appeal
          this.scheduleRefresh()
        } catch (e) {
          this.showErrorMessage(supportErrorText(e))
          this.scheduleRefresh()
        } finally {
          this.solving = false
        }
      },
      async rate ({ score, comment }) {
        this.rating = true
        try {
          const rating = await supportApi.rate(this.id, score, comment)
          this.appeal = { ...this.appeal, rating: { score: rating.score, comment: rating.comment } }
        } catch (e) {
          // already_rated - коллега успел раньше: показываем его оценку.
          if (supportErrorCode(e) !== 'already_rated') this.showErrorMessage(supportErrorText(e))
          this.scheduleRefresh()
        } finally {
          this.rating = false
        }
      },
      openMenu (target) {
        this.menu = target
      },
      async menuAction ({ action, message, attachment }) {
        if (action === 'reply') {
          this.replyTo = message
        } else if (action === 'copy') {
          try {
            await navigator.clipboard.writeText(message.text)
            this.showSuccessMessage('Текст скопирован')
          } catch (e) {
            this.showErrorMessage('Не удалось скопировать текст')
          }
        } else if (action === 'save') {
          await this.saveImage(attachment)
        }
      },
      async saveImage (attachment) {
        try {
          window.open(await supportApi.downloadUrl(attachment.id), '_blank', 'noopener')
        } catch (e) {
          this.showErrorMessage(supportErrorText(e, 'Не удалось сохранить изображение'))
        }
      },
      openOther (item) {
        this.$router.push({ name: RouteName.SupportAppeal, params: { id: item.id } })
      },
      goToList () {
        this.$router.push({ name: RouteName.SupportAppeals })
      },
    },
  }
</script>

<style scoped lang="less">
@import '../../components/support/tokens.less';

.support-appeal {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 110px);
  min-height: 560px;
  padding-top: 16px;
}

.support-appeal__state {
  padding: 80px 16px;
  text-align: center;
}

.support-appeal__state-title {
  font-size: 16px;
  font-weight: 500;
  color: @fakt-text;
}

.support-appeal__state-text {
  margin-top: 6px;
  font-size: 14px;
  color: @fakt-text-2;
}

.support-appeal__state-btn {
  margin-top: 16px;
  background-color: @fakt-surface !important;
}

.support-appeal__path {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 24px;
}

.support-appeal__back {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  color: @fakt-text;
  background: @fakt-surface;
}

.support-appeal__slash {
  color: @fakt-text-3;
}

.support-appeal__title {
  font-size: 24px;
  font-weight: 500;
  color: @fakt-text;
}

.support-appeal__chip {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  color: @fakt-text;
  background: @fakt-white;
}

.support-appeal__chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: @fakt-text-3;

  &--new { background: @sup-new; }
  &--work { background: @sup-work; }
  &--wait { background: @sup-wait; }
  &--done { background: @sup-done; }
}

.support-appeal__subject {
  font-size: 14px;
  color: @fakt-text-2;
}

.support-appeal__grid {
  display: flex;
  flex: 1;
  gap: 16px;
  min-height: 0;
}

.support-appeal__chat {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.support-appeal__panel {
  flex: 0 0 340px;
}

// Узкий экран: панель уходит под переписку
.media-lg({
  .support-appeal {
    height: auto;
  }

  .support-appeal__grid {
    flex-direction: column;
  }

  .support-appeal__chat {
    height: calc(100vh - 140px);
  }

  .support-appeal__panel {
    flex-basis: auto;
  }
});
</style>
